'use client';

import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useRef, type ReactNode, type Ref } from 'react';
import { cn } from '@/lib/utils';

const LOAD_OLDER_THRESHOLD_PX = 96;
const NEAR_BOTTOM_THRESHOLD_PX = 120;

type VirtualTicketMessageListProps<T extends { messageId: string }> = {
  messages: T[];
  hasMoreOlder: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => Promise<boolean | void>;
  renderMessage: (message: T, index: number) => ReactNode;
  scrollToBottomKey?: string | null;
  scrollToBottomSignal?: number;
  onAtBottomChange?: (atBottom: boolean) => void;
  className?: string;
  listClassName?: string;
  endRef?: Ref<HTMLDivElement>;
  topSlot?: ReactNode;
};

export function VirtualTicketMessageList<T extends { messageId: string }>({
  messages,
  hasMoreOlder,
  loadingOlder,
  onLoadOlder,
  renderMessage,
  scrollToBottomKey,
  scrollToBottomSignal = 0,
  onAtBottomChange,
  className,
  listClassName,
  endRef,
  topSlot,
}: VirtualTicketMessageListProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  const prependingRef = useRef(false);
  const lastScrollToBottomKeyRef = useRef<string | null | undefined>(undefined);

  const rowVirtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 96,
    overscan: 8,
    getItemKey: (index) => messages[index]?.messageId ?? index,
  });

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    atBottomRef.current = true;
    onAtBottomChange?.(true);
  }, [onAtBottomChange]);

  useEffect(() => {
    if (!scrollToBottomKey || messages.length === 0) return;
    if (lastScrollToBottomKeyRef.current !== scrollToBottomKey) {
      lastScrollToBottomKeyRef.current = scrollToBottomKey;
      requestAnimationFrame(() => scrollToBottom('auto'));
    }
  }, [scrollToBottomKey, messages.length, scrollToBottom]);

  useEffect(() => {
    if (!scrollToBottomSignal || !atBottomRef.current) return;
    requestAnimationFrame(() => scrollToBottom('smooth'));
  }, [scrollToBottomSignal, scrollToBottom]);

  const handleScroll = useCallback(async () => {
    const el = scrollRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distanceFromBottom <= NEAR_BOTTOM_THRESHOLD_PX;
    if (atBottomRef.current !== atBottom) {
      atBottomRef.current = atBottom;
      onAtBottomChange?.(atBottom);
    }

    if (
      el.scrollTop <= LOAD_OLDER_THRESHOLD_PX &&
      hasMoreOlder &&
      !loadingOlder &&
      !prependingRef.current &&
      messages.length > 0
    ) {
      prependingRef.current = true;
      const prevHeight = el.scrollHeight;
      const didPrepend = await onLoadOlder();
      if (didPrepend) {
        requestAnimationFrame(() => {
          const nextEl = scrollRef.current;
          if (nextEl) {
            nextEl.scrollTop = nextEl.scrollHeight - prevHeight;
          }
          prependingRef.current = false;
        });
      } else {
        prependingRef.current = false;
      }
    }
  }, [hasMoreOlder, loadingOlder, messages.length, onAtBottomChange, onLoadOlder]);

  return (
    <div
      ref={scrollRef}
      onScroll={() => void handleScroll()}
      className={cn('min-h-0 flex-1 overflow-y-auto', className)}
      style={{
        background:
          'radial-gradient(circle at top right, color-mix(in oklab, var(--primary-to) 8%, transparent), transparent 26%), var(--muted)',
      }}
    >
      <div className={cn('px-5 py-6', listClassName)}>
        {topSlot ?? (
          <div className="mb-5 text-center">
            <span className="inline-flex rounded-full border border-border bg-white px-3 py-1.5 text-xs font-bold text-muted-foreground">
              Ticket Conversation
            </span>
          </div>
        )}
        {(hasMoreOlder || loadingOlder) && (
          <div className="mb-4 text-center">
            <span className="inline-flex rounded-full border border-border bg-white px-3 py-1.5 text-xs font-bold text-muted-foreground">
              {loadingOlder ? 'Loading earlier messages…' : 'Scroll up for earlier messages'}
            </span>
          </div>
        )}
        <div
          className="relative w-full"
          style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const message = messages[virtualRow.index];
            if (!message) return null;
            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                className="absolute top-0 left-0 w-full pb-4"
                style={{ transform: `translateY(${virtualRow.start}px)` }}
              >
                {renderMessage(message, virtualRow.index)}
              </div>
            );
          })}
        </div>
        <div ref={endRef} className="h-px w-full shrink-0" aria-hidden />
      </div>
    </div>
  );
}
